import { useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '../../../lib/api';
import { useTurnstileGate } from '../../../lib/useAbuseConfig';
import { DeferredTurnstileWidget } from '../../../components/TurnstileWidget';
import { contactPageSchema, Seo } from '../../../lib/seo';
import { LegalLayout } from '../../../components/LegalLayout';
import { Button, Field, Input } from '../../../components/ui';
import { useLegal } from './useLegal';
import { useLegalPagesVisible } from '../../../lib/useLegalPagesVisible';
import { socialLinkList } from '../../../lib/socialLinks';

/** Contact page — eCommerce-Directive-compliant identity + reachable contact channels. */
export function ContactPage() {
  const c = useLegal();
  const { isVisible } = useLegalPagesVisible();
  const subprocessorsPage = isVisible('subprocessors');
  const socials = socialLinkList(c.socialLinks);
  return (
    <><Seo
      title="Contact"
      description={`How to reach SieveGuard — general support, billing and account questions, and privacy or GDPR requests, plus a contact form that reaches the team directly.`}
      path="/contact"
      // Machine-readable mirror of the three channels below. `vatNumber` is '' whenever the
      // operator has hidden business identity, so the helper omits vatID and the schema keeps
      // agreeing with the rendered page.
      jsonLd={[
        contactPageSchema({
          path: '/contact',
          entityName: c.legalEntityName,
          vatId: c.vatNumber || undefined,
          contactPoints: [
            { contactType: 'customer support', email: c.supportEmail },
            { contactType: 'privacy', email: c.privacyEmail },
            { contactType: 'security', email: 'security@sieveguard.com' },
          ],
        }),
      ]}
    /><LegalLayout title="Contact" lastUpdated={c.lastUpdatedAt}>
      <p>
        Three contact channels, each goes to a real inbox we monitor on business days.
      </p>

      <ContactForm />

      <h2>General support</h2>
      <p>
        <code>{c.supportEmail}</code> — product questions, technical issues, billing.
      </p>

      <h2>Privacy and GDPR</h2>
      <p>
        <code>{c.privacyEmail}</code> — data subject requests (access, rectification,
        erasure, restriction, portability, objection), DPA queries
        {subprocessorsPage && <>, sub-processor objections</>}.
      </p>

      <h2>Security disclosures</h2>
      <p>
        <code>security@sieveguard.com</code> — responsible disclosure of vulnerabilities.
        Our policy is published at{' '}
        <a href="/.well-known/security.txt" target="_blank" rel="noreferrer">
          /.well-known/security.txt
        </a>{' '}
        per <a href="https://www.rfc-editor.org/rfc/rfc9116" target="_blank" rel="noreferrer">RFC 9116</a>.
      </p>

      {socials.length > 0 && (
        <>
          <h2>Follow SieveGuard</h2>
          <p>
            {socials.map((s, i) => (
              <span key={s.key}>
                {i > 0 && ' · '}
                <a href={s.url} target="_blank" rel="me noopener noreferrer">
                  {s.label}
                </a>
              </span>
            ))}
          </p>
        </>
      )}

      {/* Postal address + Business identity are the personal-detail heavy sections.
          Skip both when the operator has hidden business identity — legal /
          regulatory contact still has the emails + IDPC pointer above, and
          governing law + website are still surfaced in the identity table below. */}
      {!c.hideBusinessIdentity && (
        <>
          <h2>Postal address</h2>
          <p>
            <strong>{c.legalEntityName}</strong>
            <br />
            {c.registeredAddressLine}
          </p>
        </>
      )}

      {!c.hideBusinessIdentity && (
        <>
          <h2>Business identity</h2>
          <table>
            <tbody>
              <tr>
                <td>Legal entity</td>
                <td>{c.legalEntityName}</td>
              </tr>
              <tr>
                <td>Type</td>
                <td>{c.companyType}</td>
              </tr>
              <tr>
                <td>VAT number</td>
                <td>{c.vatNumber}</td>
              </tr>
              {c.registrationNumber && (
                <tr>
                  <td>Registration number</td>
                  <td>{c.registrationNumber}</td>
                </tr>
              )}
              <tr>
                <td>Registered address</td>
                <td>{c.registeredAddressLine}</td>
              </tr>
              <tr>
                <td>Governing law</td>
                <td>{c.governingLawJurisdiction}</td>
              </tr>
              <tr>
                <td>Website</td>
                <td>
                  <a href={c.websiteUrl} target="_blank" rel="noreferrer">
                    {c.websiteUrl}
                  </a>
                </td>
              </tr>
            </tbody>
          </table>

          <h2>Supervisory authority</h2>
          <p>
            For data-protection complaints, you may also contact the Maltese{' '}
            <a href="https://idpc.org.mt" target="_blank" rel="noreferrer">
              Office of the Information and Data Protection Commissioner (IDPC)
            </a>
            , Floor 2, Airways House, High Street, Sliema SLM 1549, Malta.
          </p>
        </>
      )}
    </LegalLayout></>
  );
}

/**
 * Public contact form — POSTs to /contact (no auth). Honeypot + the global IP throttler
 * keep spam tolerable; the message is emailed to the platform support address via the
 * super-admin's configured SMTP relay.
 */
function ContactForm() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  // Honeypot — invisible to humans; bots happily fill it.
  const [website, setWebsite] = useState('');
  const [done, setDone] = useState(false);
  const gate = useTurnstileGate();

  const submit = useMutation({
    mutationFn: () =>
      api<{ ok: true }>('/contact', {
        method: 'POST',
        auth: false,
        body: { name, email, subject: subject || undefined, message, website },
        headers: gate.headers,
      }),
    onSuccess: () => {
      setDone(true);
      setName('');
      setEmail('');
      setSubject('');
      setMessage('');
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    submit.mutate();
  }

  if (done) {
    return (
      <div className="not-prose mt-8 rounded-2xl border border-[rgba(70,201,138,.4)] bg-[rgba(70,201,138,.06)] p-5 text-[14px] text-allow">
        <strong>Message sent.</strong> Thanks — we&apos;ll reply to the email address you
        provided within one business day.
      </div>
    );
  }

  return (
    <div className="not-prose mt-8 rounded-2xl border border-bordersoft bg-panel2 p-5">
      <h2 className="font-display text-xl font-bold text-txt">Send a message</h2>
      <p className="mt-1 text-[13px] text-muted">
        Goes straight to <code>{useLegal().supportEmail}</code>.
      </p>
      <form onSubmit={onSubmit} {...gate.armProps} className="mt-4 grid gap-3 sm:grid-cols-2">
        <Field label="Your name">
          <Input value={name} onChange={(e) => setName(e.target.value)} required maxLength={120} />
        </Field>
        <Field label="Email">
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            maxLength={255}
          />
        </Field>
        <Field label="Subject (optional)">
          <Input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            maxLength={200}
            placeholder="What is this about?"
          />
        </Field>
        <div className="sm:col-span-2">
          <Field label="Message">
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
              minLength={10}
              maxLength={5000}
              rows={6}
              className="w-full rounded-[10px] border border-border bg-bg p-3 text-sm text-txt outline-none transition placeholder:text-faint focus:border-accentdeep"
            />
          </Field>
        </div>
        {/* Honeypot — hidden from sighted users; CSS off-screen so screen-readers skip too. */}
        <label className="hidden" aria-hidden="true">
          Website
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
          />
        </label>
        {gate.siteKey && (
          <div className="sm:col-span-2">
            <DeferredTurnstileWidget gate={gate} />
          </div>
        )}
        <div className="sm:col-span-2 flex items-center gap-3 pt-2">
          <Button type="submit" variant="primary" disabled={submit.isPending || !gate.ready}>
            {submit.isPending ? 'Sending…' : 'Send message'}
          </Button>
          {submit.isError && (
            <span className="text-[13px] text-review">
              {submit.error instanceof Error ? submit.error.message : 'Failed to send.'}
            </span>
          )}
        </div>
      </form>
    </div>
  );
}

// Default export required by React Router framework-mode route modules.
export default ContactPage;
